import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AnnotationEditor } from "../components/AnnotationEditor";
import { AnnotationOverlay } from "../components/AnnotationOverlay";
import { Discussion } from "../components/Discussion";
import { DohaTagEditor } from "../components/DohaTagEditor";
import { EmptyState, Loading } from "../components/StateViews";
import { trpc } from "../trpc";

export function CaptureViewPage() {
  const { id } = useParams();
  const captureId = Number(id);
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);

  const query = trpc.captures.get.useQuery(
    { id: captureId },
    { enabled: Number.isFinite(captureId) },
  );

  const del = trpc.captures.delete.useMutation({
    onSuccess: () => navigate("/study"),
  });

  if (query.isLoading) return <Loading label="Opening the capture" />;
  if (!query.data) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-20 sm:px-8">
        <EmptyState title="Capture not found.">
          <Link to="/study" className="text-terracotta hover:underline">
            Back to your study
          </Link>
        </EmptyState>
      </div>
    );
  }

  const { capture, annotations } = query.data;
  const linked = annotations.filter((a) => a.status === "linked");
  const undefinedWords = annotations.filter((a) => a.status === "undefined");
  const doha = capture.doha;

  if (editing) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-14 sm:px-8">
        <AnnotationEditor
          captureId={capture.id}
          mediaPath={capture.mediaPath}
          dohaSlug={doha?.slug ?? null}
          onDone={() => {
            setEditing(false);
            void utils.captures.get.invalidate({ id: capture.id });
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 pt-14 sm:px-8">
      <Link
        to="/study"
        className="eyebrow transition-colors hover:text-terracotta"
      >
        ← Your study
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_20rem]">
        <div>
          <AnnotationOverlay
            mediaPath={capture.mediaPath}
            annotations={annotations}
          />
          {capture.isOwner ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-4 rounded-full border border-line-strong px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk"
            >
              Mark more words
            </button>
          ) : null}
        </div>

        <div className="space-y-10">
          <header className="border-b border-line pb-6">
            <DohaTagEditor
              captureId={capture.id}
              doha={doha}
              canEdit={capture.isOwner}
            />
            <p className="mt-2 text-sm text-ink-faint">{capture.language}</p>
          </header>

          <section>
            <h2 className="eyebrow">Words marked</h2>
            {linked.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {linked.map((a) => (
                  <span
                    key={a.id}
                    className="rounded-full border border-line bg-paper-raised px-3 py-1 font-serif text-sm text-ink"
                  >
                    {a.label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-ink-faint">
                No words linked to a meaning yet.
              </p>
            )}
          </section>

          {undefinedWords.length > 0 ? (
            <section>
              <h2 className="eyebrow">Waiting on a definition</h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {undefinedWords.map((a) => (
                  <span
                    key={a.id}
                    className="rounded-full border border-dashed border-terracotta/50 bg-terracotta-wash px-3 py-1 font-serif text-sm text-terracotta"
                  >
                    {a.label}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {capture.isOwner ? (
            <div className="border-t border-line pt-6">
              <button
                type="button"
                onClick={() => del.mutate({ id: capture.id })}
                disabled={del.isPending}
                className="text-sm text-ink-faint transition-colors hover:text-terracotta disabled:opacity-50"
              >
                {del.isPending ? "Deleting…" : "Delete this capture"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {undefinedWords.length > 0 ? (
        <div className="mt-16 space-y-12 border-t border-line pt-10">
          {undefinedWords.map((a) => (
            <section key={a.id}>
              <h2 className="font-serif text-2xl text-ink">
                {a.label}{" "}
                <span className="text-ink-faint">— needs a definition</span>
              </h2>
              <p className="mt-1 text-sm text-ink-soft">
                A translator can answer here.
              </p>
              <div className="mt-5">
                <Discussion subjectType="annotation" subjectId={a.id} />
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
